/**
 * Step 1: Configurações do Modelo
 * Inbox, nome, categoria, idioma e classificação
 */

import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { OfficialInbox, TemplateClassification, CATEGORIES, LANGUAGES } from './types';

interface TemplateConfigStepProps {
  // Valores
  inboxId: string;
  name: string;
  category: string;
  language: string;
  classificationId: string | null;
  
  // Callbacks
  onInboxIdChange: (value: string) => void;
  onNameChange: (value: string) => void;
  onCategoryChange: (value: string) => void;
  onLanguageChange: (value: string) => void;
  onClassificationIdChange: (value: string | null) => void;
  
  // Dados
  inboxes: OfficialInbox[];
  classifications: TemplateClassification[];
  
  // Estados
  isEditing?: boolean;
}

export function TemplateConfigStep({
  inboxId,
  name,
  category,
  language,
  classificationId,
  onInboxIdChange,
  onNameChange,
  onCategoryChange,
  onLanguageChange,
  onClassificationIdChange,
  inboxes,
  classifications,
  isEditing = false,
}: TemplateConfigStepProps) {
  
  /**
   * Formata o nome do template (apenas letras minúsculas, números e underscore)
   */
  const handleNameChange = (value: string) => {
    const formatted = value.toLowerCase().replace(/[^a-z0-9_]/g, '_');
    onNameChange(formatted);
  };
  
  return (
    <div className="space-y-6">
      {/* Inbox e Nome */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Inbox WhatsApp Oficial *</Label>
          <Select 
            value={inboxId} 
            onValueChange={onInboxIdChange}
            disabled={isEditing}
          >
            <SelectTrigger>
              <SelectValue placeholder="Selecione uma inbox..." />
            </SelectTrigger>
            <SelectContent>
              {inboxes.map(inbox => (
                <SelectItem key={inbox.id} value={inbox.id}>
                  {inbox.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[10px] text-muted-foreground">
            Somente inboxes Meta WABA são exibidas
          </p>
        </div>

        <div className="space-y-2">
          <Label>Nome do Template *</Label>
          <Input
            value={name}
            onChange={(e) => handleNameChange(e.target.value)}
            placeholder="ex: welcome_message"
            maxLength={512}
            disabled={isEditing}
          />
          <p className="text-[10px] text-muted-foreground">
            Apenas letras minúsculas, números e underscore
          </p>
        </div>
      </div>

      {/* Categoria e Idioma */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Categoria *</Label>
          <Select value={category} onValueChange={onCategoryChange}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CATEGORIES.map(cat => (
                <SelectItem key={cat.value} value={cat.value}>
                  <div className="flex items-center gap-2">
                    <span>{cat.label}</span>
                    <span className="text-xs text-muted-foreground">
                      - {cat.description}
                    </span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Idioma *</Label>
          <Select value={language} onValueChange={onLanguageChange}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LANGUAGES.map(lang => (
                <SelectItem key={lang.value} value={lang.value}>
                  {lang.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Classificação (opcional) */}
      <div className="space-y-2">
        <Label>Classificação (Opcional)</Label>
        <Select 
          value={classificationId || 'none'} 
          onValueChange={(v) => onClassificationIdChange(v === 'none' ? null : v)}
        >
          <SelectTrigger>
            <SelectValue placeholder="Selecione uma classificação" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Sem classificação</SelectItem>
            {classifications.map(c => (
              <SelectItem key={c.id} value={c.id}>
                <div className="flex items-center gap-2">
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: c.color }} 
                  />
                  {c.name}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-[10px] text-muted-foreground">
          Agrupa templates por uso interno (Comercial, Suporte, Cobrança, etc.)
        </p>
      </div>
    </div>
  );
}
